'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useClients, useMoney, useSale } from '@/backend/firebase/hooks';
import { useProducts } from '@/backend/firebase/hooks/useProducts';
import { useAcademy } from '@/backend/firebase/hooks/useAcademy';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { ClientSelectionSheet } from '@/components/selection/ClientSelectionSheet';
import { AnalyticsService } from '@/shared/analytics/analytics-service';
import type { CurrenciesType, ProductWithId } from '@/shared/schema';
import {
  Check,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  Sparkles,
  User,
  X,
  Package,
  GraduationCap,
  Keyboard,
} from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/card';

export default function SaleCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const { format: formatMoney, convert, rates } = useMoney();

  const accountingCurrency = profile?.currencyConfig?.accountingCurrency || 'USD';
  const businessId = profile?.id || '';

  // --- ESTADOS ---
  const prefillClientId = searchParams.get('clientId');
  const prefillCourseId = searchParams.get('courseId');
  const prefillProductId = searchParams.get('productId');
  const [selectedClientId, setSelectedClientId] = useState<string>(prefillClientId || '');
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [isClientSheetOpen, setIsClientSheetOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Custom Charge State
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');

  // --- DATA HOOKS ---
  const { data: products = [], isLoading: productsLoading } = useProducts.getAll(businessId);
  const { data: courses = [] } = useAcademy.useCourses(businessId);
  const { data: clients = [] } = useClients.getAll();
  const createSaleMutation = useSale.create();

  // --- LÓGICA DE FILTRADO ---
  const catalogItems = useMemo(() => {
     const p = products.map(item => ({ ...item, type: 'product' }));
     const c = courses.map(item => ({ ...item, type: 'course', name: item.title }));
     return [...p, ...c];
  }, [products, courses]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return catalogItems;
    return catalogItems.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [catalogItems, searchTerm]);

  const totalPrice = useMemo(() => {
    return selectedItems.reduce((acc, item) => {
        return acc + convert(item.price || 0, 'USD', accountingCurrency as any);
    }, 0);
  }, [selectedItems, convert, accountingCurrency]);

  const isReady = !!selectedClientId && selectedItems.length > 0;

  useEffect(() => {
    const targetItemId = prefillCourseId || prefillProductId;
    if (!targetItemId || selectedItems.length > 0 || catalogItems.length === 0) return;

    const match = catalogItems.find((item) => item.id === targetItemId);
    if (match) {
      setSelectedItems([{ ...match, quantity: 1 }]);
    }
  }, [prefillCourseId, prefillProductId, catalogItems, selectedItems.length]);

  const toggleItem = (item: any) => {
    const exists = selectedItems.find(i => i.id === item.id);
    if (exists) {
        setSelectedItems(prev => prev.filter(i => i.id !== item.id));
    } else {
        setSelectedItems(prev => [...prev, { ...item, quantity: 1 }]);
    }
  };

  const addCustomCharge = () => {
    if (!customName || !customPrice) return toast.error('Completa los datos del cargo');
    const newItem = {
        id: `custom-${Date.now()}`,
        name: customName,
        price: parseFloat(customPrice),
        type: 'custom',
        is_custom: true,
        quantity: 1
    };
    setSelectedItems(prev => [...prev, newItem]);
    setCustomName('');
    setCustomPrice('');
    setIsCustomMode(false);
    toast.success('Cargo personalizado añadido');
  };

  const handleFinalize = async () => {
    if (!isReady || !rates) return toast.error('Faltan datos');

    try {
      const client = clients.find(c => c.id === selectedClientId);
      const items = selectedItems.map((item) => ({
        serviceId: item.type === 'course' ? null : item.id,
        courseId: item.type === 'course' ? item.id : null,
        name: item.name,
        is_custom: item.is_custom || false,
        price: item.price || 0,
        currency: 'USD' as CurrenciesType,
        quantity: 1,
        type: item.type, // 'product' | 'course' | 'custom'
        material_cost_applied: 0,
        material_cost_currency: accountingCurrency as CurrenciesType,
        price_accounting_ref: 0,
        commission_percentage_applied: 0,
        commission_amount_accounting: 0,
      }));

      const newSale = await createSaleMutation.mutateAsync({
        creatorId: businessId,
        rates: rates,
        data: {
          professionalId: businessId,
          businessId: businessId,
          accounting_currency: accountingCurrency,
          client: { 
            uid: selectedClientId, 
            name: client?.name || 'Cliente', 
            phone: client?.phone || '' 
          },
          items: items,
          payments: [],
        },
      });

      AnalyticsService.track('Purchase', {
        targets: ['posthog', 'meta'],
        salonId: businessId,
        userData: { name: client?.name, phone: client?.phone, external_id: selectedClientId },
        customData: {
          value: newSale.total_accounting,
          currency: accountingCurrency as any,
          content_name: selectedItems.map(p => p.name).join(', '),
          order_id: newSale.id,
        }
      });

      toast.success('¡Venta registrada!');
      navigate(`/app/venta/${newSale.id}`);
    } catch (e) {
      toast.error('Error al procesar la venta');
    }
  };

  return (
    <div className="bg-zinc-50 min-h-screen pb-40 italic font-[var(--font-family)]">
      <main className="mx-auto max-w-md px-4 pt-6 space-y-6">
        <PageHeader title="Nueva Venta" subtitle="Cierre de Lead" showBack />

        {/* 1. SELECCIÓN DE CLIENTE */}
        <section className="space-y-2">
           <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest ml-1">1. Prospecto / Cliente</label>
           <button 
             onClick={() => setIsClientSheetOpen(true)}
             className={cn(
               "w-full flex items-center justify-between p-5 rounded-[2rem] border-none shadow-sm transition-all active:scale-[0.98]",
               selectedClientId ? "bg-zinc-900 text-white shadow-xl" : "bg-white text-zinc-400"
             )}
           >
              <div className="flex items-center gap-4">
                 <div className={cn("p-2 rounded-xl", selectedClientId ? "bg-white/20" : "bg-zinc-50")}>
                    <User className="h-5 w-5" />
                 </div>
                 <span className="text-sm font-black uppercase italic tracking-tighter">
                   {clients.find(c => c.id === selectedClientId)?.name || "Elegir Persona"}
                 </span>
              </div>
              <ChevronRight className="h-5 w-5 opacity-30" />
           </button>
        </section>

        {/* 2. SELECCIÓN DE ITEMS O CARGO MANUAL */}
        <section className="space-y-4">
           <div className="flex items-center justify-between px-1">
              <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">2. Items de Venta</label>
              <button 
                onClick={() => setIsCustomMode(!isCustomMode)}
                className="text-[9px] font-black text-indigo-600 uppercase flex items-center gap-1"
              >
                {isCustomMode ? <X className="h-3 w-3" /> : <Keyboard className="h-3 w-3" />}
                {isCustomMode ? 'Cerrar' : 'Cargo Manual'}
              </button>
           </div>

           {isCustomMode ? (
             <Card className="rounded-[2rem] border-none bg-indigo-50 p-6 space-y-4 animate-in zoom-in-95">
                <div className="space-y-2">
                   <Label className="text-[8px] font-black uppercase text-indigo-400 tracking-widest">Descripción del Cargo</Label>
                   <Input 
                     placeholder="Ej: Asesoría Personalizada" 
                     value={customName}
                     onChange={(e) => setCustomName(e.target.value)}
                     className="h-12 rounded-xl border-indigo-100 bg-white font-bold"
                   />
                </div>
                <div className="space-y-2">
                   <Label className="text-[8px] font-black uppercase text-indigo-400 tracking-widest">Precio en USD</Label>
                   <Input 
                     type="number"
                     placeholder="0.00" 
                     value={customPrice}
                     onChange={(e) => setCustomPrice(e.target.value)}
                     className="h-12 rounded-xl border-indigo-100 bg-white font-bold"
                   />
                </div>
                <Button onClick={addCustomCharge} className="w-full h-12 rounded-xl bg-indigo-600 text-white font-black uppercase tracking-widest text-[10px]">
                   Añadir Cargo
                </Button>
             </Card>
           ) : (
             <div className="space-y-3">
                <div className="relative">
                   <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-300" />
                   <Input 
                     placeholder="Buscar Producto o Curso..."
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     className="h-14 rounded-2xl border-none bg-white pl-12 text-sm font-bold shadow-sm"
                   />
                </div>

                <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto no-scrollbar pb-4">
                   {filteredItems.map((item) => {
                     const isSelected = selectedItems.find(i => i.id === item.id);
                     return (
                       <button
                         key={item.id}
                         onClick={() => toggleItem(item)}
                         className={cn(
                           "flex items-center justify-between p-4 rounded-2xl border-2 transition-all active:scale-[0.98]",
                           isSelected ? "border-emerald-500 bg-emerald-50 shadow-md" : "border-transparent bg-white shadow-sm"
                         )}
                       >
                         <div className="flex items-center gap-3 text-left">
                            <div className={cn("p-2 rounded-xl", isSelected ? "bg-emerald-500 text-white" : "bg-zinc-50 text-zinc-400")}>
                               {item.type === 'course' ? <GraduationCap className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                            </div>
                            <div>
                               <p className="text-xs font-black uppercase italic tracking-tighter text-zinc-900">{item.name}</p>
                               <p className="text-[10px] font-bold text-zinc-400">${item.price} • {item.type === 'course' ? 'CURSO' : 'PRODUCTO'}</p>
                            </div>
                         </div>
                         {isSelected && <Check className="h-5 w-5 text-emerald-600" strokeWidth={3} />}
                       </button>
                     );
                   })}
                </div>
             </div>
           )}
        </section>

        {/* 3. RESUMEN DE SELECCIÓN */}
        {selectedItems.length > 0 && (
          <section className="space-y-2 animate-in fade-in">
             <label className="text-[10px] font-black uppercase text-zinc-400 tracking-widest ml-1">Resumen de Venta</label>
             <div className="bg-white rounded-[2rem] p-4 shadow-sm divide-y divide-zinc-50">
                {selectedItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-3">
                     <div className="flex items-center gap-2">
                        <button onClick={() => toggleItem(item)} className="p-1 text-rose-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                        <span className="text-[11px] font-black uppercase italic text-zinc-700">{item.name}</span>
                     </div>
                     <span className="text-sm font-black italic text-zinc-900">${item.price}</span>
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      {/* BOTÓN DE CIERRE */}
      {isReady && (
        <div className="fixed bottom-20 left-0 right-0 px-4 z-50">
          <Button
            onClick={handleFinalize}
            disabled={createSaleMutation.isPending}
            className="h-20 w-full max-w-md mx-auto rounded-[2.5rem] bg-emerald-500 text-white shadow-2xl border-none flex items-center justify-between px-8 active:scale-95 transition-all"
          >
            <div className="flex flex-col items-start leading-tight">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70">Confirmar Todo</span>
                <span className="text-xl font-black italic uppercase tracking-tighter">Registrar Venta</span>
            </div>
            <div className="flex items-center gap-3">
                <span className="text-2xl font-black italic tracking-tighter">{formatMoney(totalPrice, 'USD')}</span>
                {createSaleMutation.isPending ? <Loader2 className="h-6 w-6 animate-spin" /> : <Check className="h-6 w-6" strokeWidth={4} />}
            </div>
          </Button>
        </div>
      )}

      <ClientSelectionSheet
        isOpen={isClientSheetOpen}
        onOpenChange={setIsClientSheetOpen}
        clients={clients}
        selectedClientId={selectedClientId}
        onSelect={(c) => setSelectedClientId(c.id)}
      />
    </div>
  );
}
